import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { notificationsAPI, analyticsAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Wrench, Users, Truck, Bell, Package, GraduationCap, ShieldCheck,
  ShoppingCart, Factory, FileText, FolderKanban, ChevronRight,
  AlertTriangle, Clock, CheckCircle, TrendingUp, UserCheck,
  RefreshCw, Cog, LogOut, Key, GitFork, HelpCircle, FileSignature, Briefcase
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';

const ERP_APPS = [
  {
    id: 'calibration_cms',
    name: 'Quality Management',
    description: 'QC, MRB, calibration instruments & records',
    icon: ShieldCheck,
    path: '/quality',
    color: 'bg-teal-500',
    available: true,
  },
  {
    id: 'supplier_mgmt',
    name: 'Supplier Management',
    description: 'Manage suppliers, approvals, and evaluations',
    icon: Truck,
    path: '/suppliers',
    color: 'bg-emerald-500',
    available: true,
  },
  {
    id: 'user_mgmt',
    name: 'User Management',
    description: 'Manage users, roles, and permissions',
    icon: Users,
    path: '/users',
    color: 'bg-purple-500',
    available: true,
    adminOnly: true,
  },
  {
    id: 'inventory_mgmt',
    name: 'Inventory',
    description: 'Items, godowns, stock, receive & issue',
    icon: Package,
    path: '/inventory',
    color: 'bg-orange-500',
    available: true,
  },
  {
    id: 'hr_training',
    name: 'HR & Training',
    description: 'Manage employee training records',
    icon: GraduationCap,
    path: '/hr',
    color: 'bg-pink-500',
    available: true,
  },
  {
    id: 'recruitment',
    name: 'Recruitment',
    description: 'Job postings, applicants, tests & hiring',
    icon: Briefcase,
    path: '/hr/recruitment',
    color: 'bg-fuchsia-500',
    available: true,
  },
  {
    id: 'purchase',
    name: 'Purchase',
    description: 'Indent, RFQ, purchase requests & orders',
    icon: ShoppingCart,
    path: '/purchase',
    color: 'bg-cyan-500',
    available: true,
  },
  {
    id: 'inventory_mgmt_production',
    name: 'Production',
    description: 'Work orders, routes, BOM management',
    icon: Factory,
    path: '/production',
    color: 'bg-amber-500',
    available: true,
  },
  {
    id: 'subcontract_mgmt',
    name: 'Subcontracting',
    description: 'Outsourcing challans, job work, service entries',
    icon: GitFork,
    path: '/subcontract',
    color: 'bg-violet-500',
    available: true,
  },
  {
    id: 'contract_mgmt',
    name: 'Contract Management',
    description: 'Customer master, sale orders, contracts',
    icon: FileText,
    path: '/contracts',
    color: 'bg-red-500',
    available: true,
  },
  {
    id: 'dispatch_mgmt',
    name: 'Dispatch',
    description: 'Delivery challans, shipments, invoice reconciliation',
    icon: Truck,
    path: '/dispatch',
    color: 'bg-teal-500',
    available: true,
  },
  {
    id: 'project_mgmt',
    name: 'Program Management',
    description: 'Programs, POs, sale vs cost analytics, tasks',
    icon: FolderKanban,
    path: '/programs',
    color: 'bg-indigo-500',
    available: true,
  },
];

export default function ERPHomePage() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [notifRes, analyticsRes] = await Promise.all([
        notificationsAPI.unreadCount(),
        analyticsAPI.dashboard()
      ]);
      setUnreadCount(notifRes.data.count);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load dashboard data');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const handleAppClick = (app) => {
    if (!app.available) {
      toast.info(`${app.name} is coming soon!`);
      return;
    }
    if (app.adminOnly && !isAdmin) {
      toast.error('Admin access required');
      return;
    }
    navigate(app.path);
  };

  const getAppAccess = (appId) => {
    if (isAdmin) return true;
    if (appId === 'user_mgmt') return false;
    const appRoles = user?.app_roles || {};
    // Recruitment is accessible to HR & Training users and to Recruitment managers
    if (appId === 'recruitment') {
      return (appRoles.recruitment && appRoles.recruitment !== 'none') || (appRoles.hr_training && appRoles.hr_training !== 'none');
    }
    // Production and Subcontract use same access as inventory_mgmt, Purchase uses supplier_mgmt
    const lookupId = appId === 'inventory_mgmt_production' ? 'inventory_mgmt' : appId === 'subcontract_mgmt' ? 'inventory_mgmt' : appId === 'purchase' ? 'supplier_mgmt' : appId;
    return appRoles[lookupId] && appRoles[lookupId] !== 'none';
  };

  const isPurchaseManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';
  const isQualityManager = isAdmin || user?.role === 'quality_manager';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_calibration-command/artifacts/0iqwnjwv_bhpl_logo.png" 
                alt="BHPL" 
                className="w-10 h-10 rounded-lg bg-white p-1"
              />
              <div>
                <h1 className="text-xl font-bold">BHPL ERP System</h1>
                <p className="text-xs text-slate-400">Enterprise Resource Planning</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/10"
                onClick={() => navigate('/help')}
                data-testid="help-button"
                title="Help & Documentation"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/10 gap-1.5"
                  onClick={() => navigate('/admin/tools')}
                  data-testid="admin-tools-button"
                  title="Admin Tools"
                >
                  <Wrench className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs font-medium">Admin Tools</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="relative text-white hover:bg-white/10"
                onClick={() => navigate('/notifications')}
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:bg-white/10 rounded-lg px-2 py-1 transition-colors" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold">
                        {user?.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="hidden sm:block text-left">
                        <p className="text-sm font-medium">{user?.name}</p>
                        <p className="text-[10px] text-slate-400">{user?.role}</p>
                      </div>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-xs" data-testid="erp-my-profile-button" onClick={() => navigate('/profile')}>
                      <FileSignature className="w-3 h-3 mr-2" />
                      My Profile
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem className="text-xs" data-testid="erp-admin-tools-menu" onClick={() => navigate('/admin/tools')}>
                        <Wrench className="w-3 h-3 mr-2" />
                        Admin Tools
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem className="text-xs" data-testid="erp-logout-button" onClick={() => { logout(); navigate('/login'); }}>
                      <LogOut className="w-3 h-3 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#0F172A]">Welcome, {user?.name?.split(' ')[0]}</h2>
          <p className="text-slate-500 mt-1">Select an application to get started</p>
        </div>

        {/* Dashboard Analytics */}
        {analytics && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0F172A]">Dashboard Overview</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={loadData}
                className="text-xs gap-1"
              >
                <RefreshCw className="w-3 h-3" /> Refresh
              </Button>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Calibration Metrics */}
              <Card className="border-l-4 border-l-blue-500" data-testid="calibration-card">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">CALIBRATION</p>
                      <p className="text-2xl font-bold text-[#0F172A]">{analytics.calibration.active_instruments}</p>
                      <p className="text-[10px] text-slate-400">Active instruments</p>
                    </div>
                    <Wrench className="w-8 h-8 text-blue-200" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-1">
                    {analytics.calibration.recalled_instruments > 0 && (
                      <Link to="/calibration/tools?status=recalled_for_calibration" className="flex items-center gap-1 text-orange-600 text-xs font-medium hover:underline" data-testid="recalled-link">
                        <AlertTriangle className="w-3 h-3" /> {analytics.calibration.recalled_instruments} recalled
                      </Link>
                    )}
                    {analytics.calibration.overdue > 0 && (
                      <Link to="/calibration/tools?status=overdue" className="flex items-center gap-1 text-red-600 text-xs font-medium hover:underline" data-testid="overdue-cal-link">
                        <AlertTriangle className="w-3 h-3" /> {analytics.calibration.overdue} overdue
                      </Link>
                    )}
                    {analytics.calibration.due_soon > 0 && (
                      <Link to="/calibration/tools?status=due_soon" className="flex items-center gap-1 text-amber-600 text-xs font-medium hover:underline" data-testid="due-soon-cal-link">
                        <Clock className="w-3 h-3" /> {analytics.calibration.due_soon} due soon
                      </Link>
                    )}
                    {analytics.calibration.recalled_instruments === 0 && analytics.calibration.overdue === 0 && analytics.calibration.due_soon === 0 && (
                      <span className="flex items-center gap-1 text-emerald-600 text-xs">
                        <CheckCircle className="w-3 h-3" /> All up to date
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Supplier Metrics */}
              <Card className="border-l-4 border-l-emerald-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">SUPPLIERS</p>
                      <p className="text-2xl font-bold text-[#0F172A]">{analytics.suppliers.approved}</p>
                      <p className="text-[10px] text-slate-400">Approved suppliers</p>
                    </div>
                    <Truck className="w-8 h-8 text-emerald-200" />
                  </div>
                  {(analytics.suppliers.under_reevaluation > 0 || analytics.suppliers.due_for_reevaluation > 0) && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex gap-3">
                      {analytics.suppliers.under_reevaluation > 0 && (
                        <Link to="/suppliers/list?status=Under re-evaluation" className="flex items-center gap-1 text-blue-600 text-xs font-medium hover:underline">
                          <RefreshCw className="w-3 h-3" /> {analytics.suppliers.under_reevaluation} in review
                        </Link>
                      )}
                      {analytics.suppliers.due_for_reevaluation > 0 && (
                        <Link to="/suppliers/list" className="flex items-center gap-1 text-amber-600 text-xs font-medium hover:underline">
                          <Clock className="w-3 h-3" /> {analytics.suppliers.due_for_reevaluation} due
                        </Link>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Metrics */}
              <Card className="border-l-4 border-l-purple-500">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">USERS</p>
                      <p className="text-2xl font-bold text-[#0F172A]">{analytics.users.total}</p>
                      <p className="text-[10px] text-slate-400">Total users</p>
                    </div>
                    <Users className="w-8 h-8 text-purple-200" />
                  </div>
                  {analytics.users.pending_approval > 0 && isAdmin && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <Link to="/users" className="flex items-center gap-1 text-amber-600 text-xs font-medium hover:underline">
                        <UserCheck className="w-3 h-3" /> {analytics.users.pending_approval} pending approval
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Tasks */}
              <Card className="border-l-4 border-l-amber-500" data-testid="pending-tasks-card">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">PENDING TASKS</p>
                      <p className="text-2xl font-bold text-[#0F172A]">
                        {(isPurchaseManager ? (analytics.pending_tasks.purchase_reviews || 0) : 0) + 
                         (isQualityManager ? (analytics.pending_tasks.quality_reviews || 0) : 0) +
                         (analytics.pending_tasks.recalled_instruments || 0) +
                         (analytics.pending_tasks.overdue_calibrations || 0)}
                      </p>
                      <p className="text-[10px] text-slate-400">Actions required</p>
                    </div>
                    <Cog className="w-8 h-8 text-amber-200" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                    {analytics.pending_tasks.overdue_calibrations > 0 && (
                      <Link to="/calibration/tools?status=overdue" className="flex items-center gap-1 text-red-600 text-xs font-medium hover:underline" data-testid="pending-overdue-cal">
                        <AlertTriangle className="w-3 h-3" /> {analytics.pending_tasks.overdue_calibrations} overdue calibrations
                      </Link>
                    )}
                    {analytics.pending_tasks.recalled_instruments > 0 && (
                      <Link to="/calibration/tools?status=recalled_for_calibration" className="flex items-center gap-1 text-orange-600 text-xs font-medium hover:underline" data-testid="pending-recalled">
                        <Clock className="w-3 h-3" /> {analytics.pending_tasks.recalled_instruments} recalled instruments
                      </Link>
                    )}
                    {isPurchaseManager && analytics.pending_tasks.purchase_reviews > 0 && (
                      <Link to="/suppliers/list?status=Under re-evaluation" className="flex items-center gap-1 text-blue-600 text-xs font-medium hover:underline" data-testid="pending-purchase-reviews">
                        <TrendingUp className="w-3 h-3" /> {analytics.pending_tasks.purchase_reviews} purchase reviews
                      </Link>
                    )}
                    {isQualityManager && analytics.pending_tasks.quality_reviews > 0 && (
                      <Link to="/suppliers/list?status=Under re-evaluation" className="flex items-center gap-1 text-emerald-600 text-xs font-medium hover:underline" data-testid="pending-quality-reviews">
                        <CheckCircle className="w-3 h-3" /> {analytics.pending_tasks.quality_reviews} quality reviews
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* App Grid */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Applications</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {ERP_APPS.map((app) => {
            const hasAccess = getAppAccess(app.id);
            const Icon = app.icon;
            
            return (
              <button
                key={app.id}
                onClick={() => handleAppClick(app)}
                disabled={!app.available || (app.adminOnly && !isAdmin) || (!hasAccess && app.available)}
                className={`relative p-6 rounded-xl border text-left transition-all duration-200 ${
                  app.available && hasAccess
                    ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md cursor-pointer'
                    : 'bg-slate-100 border-slate-200 cursor-not-allowed opacity-60'
                }`}
                data-testid={`app-card-${app.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`w-12 h-12 rounded-xl ${app.color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  {!app.available && (
                    <Badge variant="secondary" className="text-[10px]">Coming Soon</Badge>
                  )}
                  {app.available && !hasAccess && !app.adminOnly && (
                    <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-400">No Access</Badge>
                  )}
                  {app.adminOnly && app.available && (
                    <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-600">Admin</Badge>
                  )}
                </div>
                <h3 className="mt-4 font-semibold text-[#0F172A]">{app.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{app.description}</p>
                {app.available && hasAccess && (
                  <div className="mt-4 flex items-center text-sm text-blue-600 font-medium">
                    Open <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <p className="text-xs text-slate-400 text-center">
            BHPL ERP System v1.0 | © 2026 BHPL. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
